import type { Database, Json, Tables } from "~~/types/supabase";
import { defineStore } from "pinia";
import type { QueryData } from "@supabase/supabase-js";

export const useEventStore = defineStore("eventStore", () => {
  const supabase = useSupabaseClient<Database>();
  const route = useRoute();
  const querySelect =
    "*, discounts(count), event_metadata_fields(count) , groups:ticket_type_groups(*), ticket_types(*, tickets(count)), event_timeslots(id, start_date, end_date, title, ticket_limit, ticket_type_ids, group_id, close_before, close_before_minutes, tickets(count)), shop_themes(*)";
  const query = supabase.from("events").select(querySelect);

  const current_event = ref(null as QueryData<typeof query>[0] | null);
  const events = ref(
    [] as (Tables<"events"> & { ticket_types: { price: number }[] })[],
  );
  const ticket_availability = ref<Record<string, boolean>>({});
  const tickets_remaining = ref<Record<string, number | null>>({});

  // The modules this activity added (redesign §2): kept in additional_settings.modules, never derived.
  const modules = computed<EventModule[]>(() => keptModules(current_event.value) ?? []);
  const hasModule = (key: EventModule) => modules.value.includes(key);
  // Writes the list (the rest of additional_settings stays); null on success.
  async function saveModules(next: EventModule[]) {
    const event = current_event.value;
    if (!event) return null;
    const all = withModules(event.additional_settings, next);
    const { error } = await supabase.from("events").update({ additional_settings: all as Json }).eq("id", event.id);
    if (!error) event.additional_settings = all as Json;
    return error;
  }

  async function loadCurrentEvent(id?: string) {
    const { data } = await supabase.from("events").select(querySelect).eq(
      "id",
      id ?? (current_event.value?.id ?? ""),
    )
      .is("ticket_types.tickets.cancelled_at", null)
      .is("ticket_types.tickets.swapped_ticket_id", null)
      .is("event_timeslots.tickets.cancelled_at", null)
      .is("event_timeslots.tickets.swapped_ticket_id", null)
      // A test ticket takes no place (20261024110000); held places do
      .neq("ticket_types.tickets.channel", "test")
      .neq("event_timeslots.tickets.channel", "test")
      // a voucher for an extra takes no place either
      .eq("event_timeslots.tickets.kind", "ticket")
      .order("rank", { referencedTable: "ticket_types", ascending: true })
      .order("rank", { referencedTable: "ticket_type_groups", ascending: true })
      .order("start_date", { referencedTable: "event_timeslots" })
      .single();
    if (!data) return;
    // An activity of another organization of this user (a link, the all-organizations list):
    // switch first, so every right on the page is that organization's, never the previous one's
    const userStore = useUserStore();
    if (data.organization_id !== userStore.organization?.id && !userStore.admin_mode) {
      userStore.organization = { id: data.organization_id } as typeof userStore.organization;
      await userStore.loadOrganization();
      if (userStore.organization?.id !== data.organization_id) return;
    }
    current_event.value = data;
    if (keptModules(data)) return;
    // Made after the backfill (20261103110000): keep what it uses now, once, by the same rule.
    // An automatic mail reaches it when it is on, not excluded and not limited to other activities.
    const { data: flows } = await supabase.from("mail_flows").select("trigger_filter")
      .eq("organization_id", data.organization_id).not("enabled_at", "is", null);
    const mailsReach = (flows ?? []).some(({ trigger_filter }) => {
      const f = (trigger_filter ?? {}) as { event_ids?: string[]; exclude_event_ids?: string[] };
      return !f.exclude_event_ids?.includes(data.id) && (!f.event_ids?.length || f.event_ids.includes(data.id));
    });
    // another activity was opened meanwhile: never write this list into that one
    if (current_event.value?.id !== data.id) return;
    const used = modulesInUse(data, userStore.organization, mailsReach);
    // without the right to write it, without seeing the flows (mailsReach would be wrong), or when
    // saving fails, it holds for this visit only
    const keep = userStore.hasRight("events.write") && userStore.hasRight("mail.read");
    if ((!keep || await saveModules(used)) && current_event.value?.id === data.id) {
      current_event.value.additional_settings = withModules(data.additional_settings, used) as Json;
    }
  }

  async function loadEvents() {
    const organizationId = useUserStore().organization?.id;
    // No organization yet (a new account): an empty id is a 400, not an empty list.
    if (!organizationId) return;
    const { data } = await supabase.from("events")
      .select("*, ticket_types(price)").eq("organization_id", organizationId);
    if (data == null) return;
    events.value = data;
  }

  const grouped_ticket_types = computed(() => {
    if (!current_event.value) return [];
    return groupedTicketTypes(
      current_event.value?.ticket_types,
      current_event.value.groups,
    );
  });

  return {
    events,
    modules,
    hasModule,
    saveModules,
    current_event,
    grouped_ticket_types,
    ticket_availability,
    tickets_remaining,
    loadEvents,
    loadCurrentEvent,
  };
}, {
  persist: true,
});
